import React from "react";
import { Button, Spin } from "antd";
import { CrownOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { push } from "react-router-redux";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import { useOfficeFeatures } from "../officeFeatures";
import { OFFICE_FEATURES, pageAccess } from "../sidebarAccess";
import { openPlanModal } from "../Subscription/planModal";
import "./featureGate.css";

export const SUPPORT_EMAIL = "support@smilepass.com";

const LABEL = OFFICE_FEATURES.reduce((acc, f) => ({ ...acc, [f.key]: f.label }), {});

/**
 * Decides, BEFORE the page mounts, whether a feature page may render for the
 * selected office. The real page is never constructed when locked: several
 * Payments pages fire their API calls from their constructors, and those answer
 * 406 without banking.
 *
 *   allow       → the page itself
 *   loading     → the layout with a spinner (first visit only; cached after)
 *   disabled    → "isn't enabled for your office"
 *   no_banking  → a blurred stand-in with the support card (Payments & AR)
 *
 * Super admins always get the page. Strings only below — there is no error
 * boundary in this app, so a bad child whites out everything.
 */
function FeatureGate({ feature, children, dispatch }) {
  const status = useOfficeFeatures();
  const decision = pageAccess(feature, status);

  if (decision === "allow") return children;

  if (decision === "loading") {
    return (
      <DashboardLayout suppressTour>
        <div className="fgate-loading"><Spin /></div>
      </DashboardLayout>
    );
  }

  if (decision === "plan_required") {
    const exhausted = status.plan && status.plan.reason === "trial_exhausted";
    return (
      <DashboardLayout suppressTour>
        <div className="fgate-stage fgate-stage--plain">
          <div className="fgate-card">
            <CrownOutlined className="fgate-icon" />
            <h3>{`Choose a plan to use ${LABEL[feature] || "this feature"}`}</h3>
            <p>
              {exhausted
                ? "You've used your free requests. Everything you've already fetched stays available."
                : "Your office doesn't have a plan yet."}
              {" Pick one for each office and you're back to work in a minute."}
            </p>
            <Button type="primary" onClick={() => openPlanModal({ reason: status.plan.reason })}>
              Choose a plan
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (decision === "no_banking") {
    return (
      <DashboardLayout suppressTour>
        <div className="fgate-stage">
          <div className="fgate-standin" aria-hidden="true">
            <div className="fgate-standin-head" />
            <div className="fgate-standin-bar" />
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div className="fgate-standin-row" key={i}>
                <span /><span /><span /><span />
              </div>
            ))}
          </div>
          <div className="fgate-card" role="dialog" aria-labelledby="fgate-title">
            <LockOutlined className="fgate-icon" />
            <h3 id="fgate-title">{`${LABEL[feature] || "This feature"} isn't set up for your office yet`}</h3>
            <p>
              {"To get access to this feature, please contact Smilepass support at "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
            <Button type="primary" icon={<MailOutlined />} href={`mailto:${SUPPORT_EMAIL}`}>
              Email support
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // disabled
  return (
    <DashboardLayout suppressTour>
      <div className="fgate-stage fgate-stage--plain">
        <div className="fgate-card">
          <LockOutlined className="fgate-icon" />
          <h3>{`${LABEL[feature] || "This feature"} isn't enabled for your office`}</h3>
          <p>{"If you'd like it turned on, ask your Smilepass contact."}</p>
          {/* "/" re-runs the login landing, which knows where this user belongs. */}
          <Button type="primary" onClick={() => dispatch(push("/"))}>Go to your dashboard</Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

const ConnectedFeatureGate = connect()(FeatureGate);
export default ConnectedFeatureGate;

/** Route helper: render={gated("payments_ar", AR)} — defined once, not inline,
 * so the route doesn't remount its page on every render. */
export const gated = (feature, Page) => {
  const GatedRoute = (props) => (
    <ConnectedFeatureGate feature={feature}>
      <Page {...props} />
    </ConnectedFeatureGate>
  );
  GatedRoute.displayName = `Gated(${feature})`;
  return GatedRoute;
};
